import { useEffect, useState } from 'react'

export function loadTopicEntry (data) {
  const topic = loadTopic(data.topic);
  const lastMessageTime = new Date(data.last_message_time);

  return createTopicEntry(topic, lastMessageTime);
}

export function createTopicEntry (topic, lastMessageTime) {
  return { ...topic, lastMessageTime };
}

export function loadTopic (data) {
    if (data == null) {
      return { id: null };
    }

    if (typeof data === 'number' || typeof data === 'string') {
      return { id: data };
    }

    const author = data.author || {};
    const photo = author.photo || {};

    return {
      id: data.id ?? data.topic_id ?? null,
      isSystem: data.is_system,
      photo: photo.path,
      author: `${author.fullname || author.login || ''}`,
      subject: data.subject
    };
}

export function loadMessage (data) {
  if (data == null) {
    return { id: null };
  }

  return {
    id: data.id ?? data.message_id,
    timestamp: new Date(data.time ?? data.timestamp),
    content: data.content ?? data.text,
    files: data.files || [],
    isRead: data.is_read,
    author: data.author,
    isDashboardShow: data.is_dashboard_show
  }
}

export function idsEqual (a, b) {
  return a != null && b != null && String(a) === String(b);
}

export function pickConversationTopicId (entry, messageId) {
  if (entry == null) {
    return null;
  }

  const rawMessage = entry.message || entry;
  const nested = entry.topic || rawMessage.topic;
  const nestedId = (nested != null && typeof nested === 'object')
    ? (nested.topic_id ?? nested.id)
    : nested;

  const candidates = [entry.topic_id, rawMessage.topic_id, nestedId];

  return candidates.find((id) => id != null && !idsEqual(id, messageId))
    ?? candidates.find((id) => id != null)
    ?? null;
}

export function resolveConversationTopic (topics, topicId, messageId, topicSubject) {
  if (!Array.isArray(topics)) {
    return null;
  }

  const byId = (id) => topics.find((topic) => idsEqual(topic.id, id));

  if (topicId != null) {
    const found = byId(topicId);
    if (found) {
      return found;
    }
  }

  if (messageId != null) {
    const asTopic = byId(messageId);
    if (asTopic) {
      return asTopic;
    }
  }

  if (topicSubject) {
    const bySubject = topics.find((topic) => topic.subject === topicSubject);
    if (bySubject) {
      return bySubject;
    }
  }

  if (topicId != null && !idsEqual(topicId, messageId)) {
    return { id: topicId };
  }

  if (messageId != null) {
    return null;
  }

  return topics[0] || null;
}

export function useMessengerTopic (topics, topicId, selectedMessageId, topicSubject) {
  const [selectedTopic, setSelectedTopic] = useState(() =>
    resolveConversationTopic(topics, topicId, selectedMessageId, topicSubject)
  );
  const [userPickedTopic, setUserPickedTopic] = useState(false);

  useEffect(() => {
    setSelectedTopic(resolveConversationTopic(topics, topicId, selectedMessageId, topicSubject));
    setUserPickedTopic(false);
  }, [topics, topicId, selectedMessageId, topicSubject]);

  return {
    selectedTopic,
    viewMessageId: userPickedTopic ? null : selectedMessageId,
    selectTopic: (topic) => {
      setSelectedTopic(topic);
      setUserPickedTopic(true);
    },
    clearTopic: () => setSelectedTopic(null)
  };
}

export function scrollChildIntoContainer (container, element, offset = 8) {
  if (container == null || element == null) {
    return;
  }

  const top =
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop;

  container.scrollTop = Math.max(0, top - offset);
}

export function isMine (visitorInfo, message) {
  return String(visitorInfo.info?.id) === String(message.author?.id);
}

export function getUnreadMessageIdsForTopic(visitorInfo, topicId, topicSubject) {
  return visitorInfo
    .listUnreadMessages()
    .filter((message) => {
      if (topicId != null && String(message.topic?.id) === String(topicId)) {
        return true;
      }

      if (topicSubject && message.topic?.subject === topicSubject) {
        return true;
      }

      return false;
    })
    .map((message) => message.id);
}
